// @flow
import {defineMessage} from 'react-intl';
import * as React from 'react';
import sampleConnectionToken from '../responses/connection_token';
import samplePaymentIntent from '../responses/payment_intent';
import samplePaymentIntentCapture from '../responses/payment_intent_capture';
import {BuildInstructions, TerminalCards} from '../../../components/left-col';

import type {StepsConfig} from '../../../data/GuideTypes';

const loadBuildInstructions = [
  {
    label: 'CocoaPods',
    body: {
      message: defineMessage({
        id:
          'docsApp.ib.terminal.ios.steps.load-stripe.buildInstructions.cocoaPods',
        defaultMessage:
          'Add this line to your Podfile, and use the .xcworkspace file to open your project in Xcode, instead of the .xcodeproj file, from here on out.',
      }),
    },
    command: `pod 'StripeTerminal', '2.0.0'`,
  },
  {
    label: 'Carthage',
    body: {
      message: defineMessage({
        id:
          'docsApp.ib.terminal.ios.steps.load-stripe.buildInstructions.carthage',
        defaultMessage: 'Add this line to your Cartfile.',
      }),
    },
    command: `github "stripe/stripe-terminal-ios"`,
  },
  {
    label: 'XCFramework',
    body: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.ios.steps.load-stripe.buildInstructions.xcf',
        defaultMessage:
          '1. Download StripeTerminal.xcframework.zip from the <a>latest release on GitHub</a>.{br}2. Unzip it and drag the .xcframework in to your project making sure to select "Copy items if needed".{br}3. Ensure the xcframework is included in the "Frameworks, Libraries, and Embedded Content" section of your application target in Xcode and set to "Embed & Sign".',
      }),
      values: {
        a: (str) => (
          <a
            href="https://github.com/stripe/stripe-terminal-ios/releases"
            target="_blank"
          >
            {str}
          </a>
        ),
        br: <br />,
      },
    },
  },
  {
    label: 'Swift Package Manager',
    body: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.ios.steps.load-stripe.buildInstructions.spm',
        defaultMessage:
          '1. In Xcode, select File > Swift Packages > Add Package Dependency from the menu bar.{br}2. Enter the Stripe Terminal iOS SDK’s Github URL:',
      }),
      values: {
        br: <br />,
      },
    },
    command: `https://github.com/stripe/stripe-terminal-ios`,
  },
];

const configureBuildInstructions = [
  {
    label: 'Location',
    body: {
      message: defineMessage({
        id:
          'docsApp.ib.terminal.ios.steps.configure-app.buildInstructions.location',
        defaultMessage:
          'Enable location services with the following key-value pair.',
      }),
    },
    command: `<key>NSLocationWhenInUseUsageDescription</key>
<string>Location access is required in order to accept payments.</string>`,
    large: true,
  },
  {
    label: 'Background Modes',
    body: {
      message: defineMessage({
        id:
          'docsApp.ib.terminal.ios.steps.configure-app.buildInstructions.backgroundModes',
        defaultMessage:
          'Ensure that your app runs in the background and remains connected to the reader.',
      }),
    },
    command: `<key>UIBackgroundModes</key>
<array>
<string>bluetooth-central</string>
</array>`,
    large: true,
  },
  {
    label: 'Bluetooth Peripheral',
    body: {
      message: defineMessage({
        id:
          'docsApp.ib.terminal.ios.steps.configure-app.buildInstructions.bluetoothPeripheral',
        defaultMessage:
          'Pass app validation checks when submitting to the App Store.',
      }),
    },
    command: `<key>NSBluetoothPeripheralUsageDescription</key>
<string>Bluetooth access is required in order to connect to supported bluetooth card readers.</string>`,
    large: true,
  },
  {
    label: 'Bluetooth Always',
    body: {
      message: defineMessage({
        id:
          'docsApp.ib.terminal.ios.steps.configure-app.buildInstructions.bluetoothAlways',
        defaultMessage:
          'Allow your app to display a Bluetooth permission dialog.',
      }),
    },
    command: `<key>NSBluetoothAlwaysUsageDescription</key>
<string>This app uses Bluetooth to connect to supported card readers.</string>`,
    large: true,
  },
];

export default ([
  {
    file: 'server',
    id: 'setup',
    section: 'setup-server',
  },
  {
    file: 'server',
    id: 'create-location',
    section: 'setup-sdk',
    heading: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.ios.steps.create-mandatory-location.heading',
        defaultMessage: 'Organize  your readers',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.ios.steps.create-mandatory-location.body',
        defaultMessage:
          '<a>Create locations</a> to organize your readers. Locations group readers and allows them to automatically download the reader configuration needed for their region of use.',
      }),
      values: {
        a: (str) => (
          <a
            href="https://stripe.com/docs/terminal/fleet/locations"
            target="_blank"
          >
            {str}
          </a>
        ),
      },
    },
  },
  {
    file: 'server',
    id: 'connection-token-endpoint',
    section: 'setup-server',
    heading: {
      message: defineMessage({
        id:
          'docsApp.ib.terminal.ios.steps.connection-token-endpoint.chipper.heading',
        defaultMessage: 'Create a ConnectionToken endpoint',
      }),
    },
    body: {
      message: defineMessage({
        id:
          'docsApp.ib.terminal.ios.steps.connection-token-endpoint.chipper.body',
        defaultMessage:
          'To connect to a reader, your backend needs to give the SDK permission to use the reader with your Stripe account by providing it with the secret from a <connectionTokenA>ConnectionToken</connectionTokenA>. Your backend should only create connection tokens for clients that it trusts. If you’re using Stripe Connect, you should also <scopeA>scope the connection token</scopeA> to the relevant connected accounts.',
      }),
      values: {
        connectionTokenA: (str) => (
          <a href="/docs/api/terminal/connection_tokens" target="_blank">
            {str}
          </a>
        ),
        scopeA: (str) => (
          <a href="/docs/terminal/features/connect" target="_blank">
            {str}
          </a>
        ),
      },
    },
    response: sampleConnectionToken,
    filter: (options) => ['chipper', 'wp3'].includes(options.reader),
  },
  {
    file: 'server',
    id: 'connection-token-endpoint',
    section: 'setup-server',
    heading: {
      message: defineMessage({
        id:
          'docsApp.ib.terminal.ios.steps.connection-token-endpoint.wpe.heading',
        defaultMessage: 'Create a ConnectionToken endpoint',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.ios.steps.connection-token-endpoint.wpe.body',
        defaultMessage:
          'To connect to a reader, your backend needs to give the SDK permission to use the reader with your Stripe account by providing it with the secret from a <connectionTokenA>ConnectionToken</connectionTokenA>. Your backend should only create connection tokens for clients that it trusts. You can <passA>pass a location ID</passA> when creating the connection token to control access to readers. If you’re using Stripe Connect, you should also <scopeA>scope the connection token</scopeA> to the relevant connected accounts.',
      }),
      values: {
        connectionTokenA: (str) => (
          <a href="/docs/api/terminal/connection_tokens" target="_blank">
            {str}
          </a>
        ),
        scopeA: (str) => (
          <a href="/docs/terminal/features/connect" target="_blank">
            {str}
          </a>
        ),
        passA: (str) => (
          <a
            href="/docs/terminal/fleet/locations#direct-connection-tokens"
            target="_blank"
          >
            {str}
          </a>
        ),
      },
    },
    response: sampleConnectionToken,
    filter: (options) => ['wpe'].includes(options.reader),
  },
  {
    id: 'load-stripe',
    section: 'setup-sdk',
    heading: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.ios.steps.load-stripe.heading',
        defaultMessage: 'Install the SDK',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.ios.steps.load-stripe.body',
        defaultMessage:
          'The iOS SDK is <a>open source</a>, fully documented, and compatible with apps supporting iOS 10 or above. Import the Stripe SDK into your checkout screen’s UIViewController.',
      }),
      values: {
        a: (str) => (
          <a
            href="https://github.com/stripe/stripe-terminal-ios"
            target="_blank"
          >
            {str}
          </a>
        ),
      },
    },
    Component() {
      return <BuildInstructions instructions={loadBuildInstructions} />;
    },
  },
  {
    id: 'configure-app',
    section: 'setup-sdk',
    heading: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.ios.steps.configure-app.heading',
        defaultMessage: 'Configure your app',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsApp.ib.terminaconfigure-appabodytion.heading',
        defaultMessage:
          'To prepare your app to work with the Stripe Terminal SDK, make a few changes to your Info.plist file in Xcode.',
      }),
    },
    Component() {
      return <BuildInstructions instructions={configureBuildInstructions} />;
    },
  },
  {
    id: 'fetch-connection-token',
    section: 'setup-sdk',
    heading: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.ios.steps.fetch-connection-token.heading',
        defaultMessage: 'Fetch ConnectionToken',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.ios.stefetch-connection-tokenibodying',
        defaultMessage:
          'Implement the ConnectionTokenProvider protocol in your app, which defines a single function that requests a connection token from your backend.',
      }),
    },
  },
  {
    id: 'sdk',
    section: 'setup-sdk',
    heading: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.ios.steps.sdk.heading',
        defaultMessage: 'Initialize the SDK',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsApp.sdk.bodyreate-location.heading',
        defaultMessage:
          'To get started, provide your ConnectionTokenProvider. You can only call <code>setTokenProvider</code> once in your app, and must call it before accessing <code>Terminal.shared</code>.',
      }),
      values: {
        code: (str) => <code>{str}</code>,
      },
    },
  },
  {
    id: 'discover-reader',
    section: 'reader',
    heading: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.ios.steps.discover-reader.chipper.heading',
        defaultMessage: 'Discover readers',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.ios.steps.discover-reader.chipper.body',
        defaultMessage:
          'The Stripe Terminal SDK comes with a built-in simulated card reader, so you can develop and test your app without connecting to physical hardware. To use the simulated reader, call discoverReaders to search for readers, with the simulated option set to true.',
      }),
    },
    filter: (options) => ['chipper', 'wp3'].includes(options.reader),
  },
  {
    id: 'discover-reader',
    section: 'reader',
    heading: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.ios.steps.discover-reader.wpe.heading',
        defaultMessage: 'Discover readers',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.ios.steps.discover-reader.wpe.body',
        defaultMessage:
          'The Stripe Terminal SDK comes with a built-in simulated card reader, so you can develop and test your app without connecting to physical hardware. To use the simulated reader, call <code>discoverReaders</code> to search for readers, with the simulated option set to true. You can discover intended readers more easily by <a>filtering by location</a>.',
      }),
      values: {
        a: (str) => (
          <a
            href="/docs/terminal/fleet/locations#internet-reader-discovery"
            target="_blank"
          >
            {str}
          </a>
        ),
        code: (str) => <code>{str}</code>,
      },
    },
    filter: (options) => ['wpe'].includes(options.reader),
  },
  {
    id: 'connect-reader',
    section: 'reader',
    heading: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.ios.steps.connect-reader.heading',
        defaultMessage: 'Connect to the simulated reader',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.ios.steps.connect-reader.body',
        defaultMessage:
          'When the <code>didUpdateDiscoveredReaders</code>  delegate method is called, call connectInternetReader to connect to the simulated reader.',
      }),
      values: {
        code: (str) => <code>{str}</code>,
      },
    },
    filter: (options) => ['wpe'].includes(options.reader),
  },
  {
    id: 'connect-reader',
    section: 'reader',
    heading: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.ios.steps.connect-bluetooth-reader.heading',
        defaultMessage: 'Connect to the simulated reader',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.ios.steps.connect-bluetooth-reader.body',
        defaultMessage:
          'When the <code>didUpdateDiscoveredReaders</code>  delegate method is called, call connectBluetoothReader to connect to the simulated reader.',
      }),
      values: {
        code: (str) => <code>{str}</code>,
      },
    },
    filter: (options) => ['wp3', 'chipper'].includes(options.reader),
  },
  {
    id: 'create-intent-mobile',
    section: 'collect-payment',
    heading: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.ios.steps.create-intent-mobile.heading',
        defaultMessage: 'Create a PaymentIntent',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.ios.steps.create-intent-mobile.body',
        defaultMessage: `Create a <a>PaymentIntent</a> object using the SDK. A PaymentIntent tracks the customer's payment lifecycle, keeping track of any failed payment attempts and ensuring the customer is only charged once.`,
      }),
      values: {
        a: (str) => (
          <a href="/docs/api/payment_intents" target="_blank">
            {str}
          </a>
        ),
      },
    },
    response: samplePaymentIntent,
    filter: (options) => ['chipper', 'wp3'].includes(options.reader),
  },
  {
    id: 'create-intent',
    file: 'server',
    section: 'collect-payment',
    heading: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.ios.steps.create-intent.heading',
        defaultMessage: 'Create a PaymentIntent',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.ios.steps.create-intent.body',
        defaultMessage: `Add an endpoint on your server that creates a PaymentIntent. A PaymentIntent tracks the customer's payment lifecycle, keeping track of any failed payment attempts and ensuring they’re only charged once. Return the PaymentIntent's client secret in the response. If you’re using Stripe Connect, you can also specify <a>connected account information</a> based on your platform’s charge logic.`,
      }),
      values: {
        a: (str) => (
          <a href="/docs/terminal/features/connect" target="_blank">
            {str}
          </a>
        ),
      },
    },
    response: samplePaymentIntent,
    filter: (options) => ['wpe'].includes(options.reader),
  },
  {
    id: 'fetch-intent',
    section: 'collect-payment',
    heading: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.ios.steps.fetch-intent.heading',
        defaultMessage: 'Fetch the PaymentIntent',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.ios.steps.fetch-intent.body',
        defaultMessage:
          'Make a request to your server for a PaymentIntent to initiate the payment process.',
      }),
    },
    filter: (options) => ['wpe'].includes(options.reader),
  },
  {
    id: 'collect-payment-method',
    section: 'collect-payment',
    heading: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.ios.steps.collect-payment-method.heading',
        defaultMessage: 'Collect payment method details',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.ios.steps.crcollect-payment-methodody',
        defaultMessage: `Call <code>collectPaymentMethod</code> with the PaymentIntent’s client secret to collect a payment method. When connected to the simulated reader calling this method immediately updates the PaymentIntent object with a <a>simulated test card</a>. When connected to a physical reader the connected reader waits for a card to be presented.`,
      }),
      values: {
        code: (str) => <code>{str}</code>,
        a: (str) => (
          <a
            href="/docs/terminal/references/testing#simulated-test-cards"
            target="_blank"
          >
            {str}
          </a>
        ),
      },
    },
  },
  {
    id: 'process-payment',
    section: 'collect-payment',
    heading: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.ios.steps.process-payment.heading',
        defaultMessage: 'Process the payment',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.process-paymentebodyon.heading',
        defaultMessage:
          'After successfully collecting payment method data, call <code>processPayment</code> with the updated PaymentIntent to process the payment. A successful call results in a PaymentIntent with a status of <code>requires_capture</code>.',
      }),
      values: {
        code: (str) => <code>{str}</code>,
      },
    },
  },
  {
    id: 'capture-intent-endpoint',
    file: 'server',
    section: 'collect-payment',
    heading: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.ios.steps.capture-intent-endpoint.heading',
        defaultMessage: 'Create an endpoint to capture the PaymentIntent',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.ios.steps.capture-intent-endpoint.body',
        defaultMessage:
          'Create an endpoint on your backend that accepts a PaymentIntent ID and sends a request to the Stripe API to capture it.',
      }),
    },
    response: samplePaymentIntentCapture,
  },
  {
    id: 'capture-intent',
    section: 'collect-payment',
    heading: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.ios.steps.capture-intent.heading',
        defaultMessage: 'Capture the PaymentIntent',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.ios.steps.capture-intent.body',
        defaultMessage:
          'Notify your backend to capture the PaymentIntent. In your request send the PaymentIntent ID.',
      }),
    },
  },
  {
    id: 'run-server',
    file: 'server',
    section: 'test-the-integration',
    heading: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.ios.steps.run-server.heading',
        defaultMessage: 'Run the application',
      }),
    },
  },
  {
    id: 'mobile-test-cards',
    section: 'test-the-integration',
    heading: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.ios.steps.mobile-test-cards.heading',
        defaultMessage: 'Make a test payment',
      }),
    },
    testing: true,
    body: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.ios.steps.mobile-test-cards.body',
        defaultMessage:
          'Use <a>amounts</a> ending in the following special values to test your integration.',
      }),
      values: {
        a: (str) => (
          <a
            href="/docs/terminal/references/testing#physical-test-card"
            target="_blank"
          >
            {str}
          </a>
        ),
      },
    },
    Component: TerminalCards,
  },
]: StepsConfig);
